import { ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Box, BoxProps } from 'reflexbox';
import { rgba } from 'polished';

import { Spinner } from './Spinner';

const Container = styled(Box)<{
	type: 'primary' | 'secondary' | 'destructive' | 'plain' | 'loading';
	small?: boolean;
}>`
	background: ${(props) => rgba(props.theme.colors.primary, 0.25)};
	border-radius: 16px;
	padding: 4px;
	cursor: pointer;

	svg {
		display: block;
		width: 24px;
		height: 24px;

		path {
			fill: ${(props) => props.theme.colors.primary};
		}
	}

	${(props) =>
		props.type === 'primary' &&
		`
        &:hover {
            background-color: ${rgba(props.theme.colors.primary, 0.4)};

            &:active {
                background-color: ${rgba(props.theme.colors.primary, 0.5)};
            }
        }
    `}

	${(props) =>
		props.type === 'secondary' &&
		`
		background: ${rgba(props.theme.colors.slateBlue, 0.25)};

		svg path {
			fill: ${props.theme.colors.slateBlue};
		}

		&:hover {
			background-color: ${rgba(props.theme.colors.slateBlue, 0.4)};

			&:active {
				background-color: ${rgba(props.theme.colors.slateBlue, 0.5)};
			}
		}
	`}

	${(props) =>
		props.type === 'plain' &&
		`
        background: transparent;

        svg path {
            fill: ${props.theme.colors.slateBlue};
        }

        &:hover {
            background-color: ${rgba(props.theme.colors.slateBlue, 0.25)};

            &:active {
                background-color: ${rgba(props.theme.colors.slateBlue, 0.4)};
            }
        }
    `}

	${(props) =>
		props.type === 'destructive' &&
		`
		background: ${rgba(props.theme.colors.danger, 0.25)};

		svg path {
			fill: ${props.theme.colors.danger};
		}

		&:hover {
			background-color: ${rgba(props.theme.colors.danger, 0.4)};

			&:active {
				background-color: ${rgba(props.theme.colors.danger, 0.5)};
			}
		}
	`}

	${(props) =>
		props.small &&
		`
		padding: 2px;

		svg {
			width: 16px;
			height: 16px;
		}
	`}

    ${(props) =>
		props.type === 'loading' && `background: ${props.theme.colors.lightBlue}; padding: 8px;`}
`;

export function IconButton(
	props: BoxProps & {
		icon?: ReactNode;
		small?: boolean;
		type?: 'primary' | 'secondary' | 'destructive' | 'plain';
		onClick?: () => Promise<void> | void;
	},
) {
	const [loading, setLoading] = useState(false);

	if (loading) {
		return (
			<Container type="loading">
				<Spinner />
			</Container>
		);
	}

	return (
		<Container
			{...props}
			type={props.type ?? 'primary'}
			onClick={async () => {
				setLoading(true);
				await props.onClick?.();
				setLoading(false);
			}}
		>
			{props.icon}
		</Container>
	);
}
